/**
 * Pure state helpers for the non-blocking background download indicator
 * (DEV-11930).
 *
 * Library downloads used to raise `app.setState({ loading: true })`, which
 * renders the full-screen blocking loader until the transfer finishes. A big
 * archive can take minutes, so the portal was unusable for the duration. These
 * helpers back a small progress card instead.
 *
 * Two things drive the shape here:
 *
 *  - Entries are keyed by id, not a single slot. Now that downloads no longer
 *    block, starting a second one while the first runs is normal — with a single
 *    slot the first one finishing would clear the second one's indicator.
 *  - State lives on the app, not on the file browser, so the indicator survives
 *    navigating away from the Library mid-transfer (and so progress callbacks
 *    firing after the file browser unmounts are harmless).
 */

// A streamed zip has no Content-Length, so percent is estimated against the
// total_size the stats call reported. Hold at 99 until the transfer actually
// completes rather than showing a premature (or >100%) 100.
const MAX_IN_FLIGHT_PERCENT = 99;

// yieldForPaint waits until the browser has actually painted the pending React
// commit.
//
// `window.confirm()` / `window.alert()` block the main thread. A setState issued
// immediately before one updates the DOM but never gets painted, so the user sees a
// stale progress card sitting behind the dialog — "Calculating download size..."
// still on screen next to a confirm that already shows the finished counts. Always
// await this between removing a card and opening a blocking dialog.
//
// Two frames, because an rAF callback runs *before* the paint it belongs to; the
// second one only fires once that frame has been painted. Falls back to a macrotask
// where rAF does not exist (jsdom).
export const yieldForPaint = () => {
	return new Promise((resolve) => {
		if (typeof requestAnimationFrame !== 'function') {
			setTimeout(resolve, 0);
			return;
		}

		requestAnimationFrame(() => {
			requestAnimationFrame(() => {
				resolve();
			});
		});
	});
};

export const downloadPercent = (receivedBytes, totalBytes) => {
	if (!totalBytes || totalBytes <= 0) {
		return null;
	}

	const percent = Math.floor((receivedBytes / totalBytes) * 100);

	return Math.max(0, Math.min(MAX_IN_FLIGHT_PERCENT, percent));
};

export const addDownload = (downloads, id, label) => {
	return {
		...downloads,
		[id]: {
			id,
			label,
			receivedBytes: 0,
			totalBytes:    0,
			percent:       null,
		},
	};
};

export const updateDownload = (downloads, id, patch) => {
	if (!downloads[id]) {
		return downloads;
	}

	return {
		...downloads,
		[id]: {
			...downloads[id],
			...patch,
		},
	};
};

export const removeDownload = (downloads, id) => {
	if (!downloads[id]) {
		return downloads;
	}

	const next = {
		...downloads,
	};
	delete next[id];

	return next;
};
